import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { 
  CheckCircle, XCircle, Clock, AlertCircle, FileText, 
  User, Calendar, DollarSign, TrendingUp, Eye, MessageSquare,
  ChevronRight, Filter, Search
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ApprovalDashboard({ user, onLogout }) {
  const [activeTab, setActiveTab] = useState('pending');
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [stats, setStats] = useState({ pending: 0, approved_by_me: 0, rejected_by_me: 0 });
  const [selectedApproval, setSelectedApproval] = useState(null);
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionType, setActionType] = useState('');
  const [comments, setComments] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');

  useEffect(() => {
    fetchStats();
    if (activeTab === 'pending') {
      fetchPendingApprovals();
    } else {
      fetchMyRequests();
    }
  }, [activeTab]);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/approvals/stats/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      setStats({ pending: 0, approved_by_me: 0, rejected_by_me: 0 });
    }
  };

  const fetchPendingApprovals = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/approvals/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingApprovals(response.data.approvals || []);
    } catch (error) {
      console.error('Error fetching pending approvals:', error);
      setPendingApprovals([]);
    }
  };

  const fetchMyRequests = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/approvals/my-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyRequests(response.data.requests || []);
    } catch (error) {
      console.error('Error fetching my requests:', error);
      setMyRequests([]);
    }
  };

  const handleAction = async () => {
    if (!selectedApproval || !actionType) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/approvals/${selectedApproval.id}/action`,
        {
          action: actionType,
          comments: comments
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setShowActionModal(false);
      setSelectedApproval(null);
      setComments('');
      fetchStats();
      if (activeTab === 'pending') {
        fetchPendingApprovals();
      } else {
        fetchMyRequests();
      }
      
      alert(`Request ${actionType}d successfully!`);
    } catch (error) {
      console.error('Error processing action:', error);
      alert(`Failed to ${actionType} request: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const openActionModal = (approval, action) => {
    setSelectedApproval(approval);
    setActionType(action);
    setShowActionModal(true);
  };

  const getRequestTypeIcon = (type) => {
    const icons = {
      leave: '🏖️',
      expense: '💳',
      hiring: '👤',
      confirmation: '✅',
      exit: '🚪',
      salary_within_band: '💰',
      salary_outside_band: '💵',
      grade_change_promotion: '⬆️',
      attendance_override: '📅'
    };
    return icons[type] || '📄';
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      approved: 'bg-green-100 text-green-800 border-green-300',
      rejected: 'bg-red-100 text-red-800 border-red-300'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${styles[status] || styles.pending}`}>
        {status.toUpperCase()}
      </span>
    );
  };

  const filteredData = activeTab === 'pending' 
    ? pendingApprovals.filter(a => 
        (filterType === 'all' || a.request_type === filterType) &&
        (searchQuery === '' || 
         a.requester_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
         a.description.toLowerCase().includes(searchQuery.toLowerCase()))
      )
    : myRequests.filter(a =>
        (filterType === 'all' || a.request_type === filterType) &&
        (searchQuery === '' || a.description.toLowerCase().includes(searchQuery.toLowerCase()))
      );

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="min-h-screen" style={{backgroundColor: '#f5f7fa'}}>
        {/* ZOBOX Header */}
        <div className="px-8 py-8" style={{
          background: 'linear-gradient(135deg, #2b3380 0%, #1a1f4d 100%)'
        }}>
          <h1 className="text-3xl font-bold text-white mb-2">✅ Approval Center</h1>
          <p className="text-gray-300">Manage approval requests and track status</p>
        </div>

        <div className="px-8 py-6 -mt-8">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Pending Approvals</p>
                  <h3 className="text-4xl font-bold text-orange-600 mb-1">{stats.pending}</h3>
                  <p className="text-sm text-gray-500">Require your action</p>
                </div>
                <div className="p-3 rounded-lg" style={{backgroundColor: '#ff9800'}}>
                  <Clock className="w-6 h-6 text-white" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Approved by You</p>
                  <h3 className="text-4xl font-bold text-green-600 mb-1">{stats.approved_by_me}</h3>
                  <p className="text-sm text-gray-500">Total approved</p>
                </div>
                <div className="p-3 rounded-lg bg-green-600">
                  <CheckCircle className="w-6 h-6 text-white" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Rejected by You</p>
                  <h3 className="text-4xl font-bold text-red-600 mb-1">{stats.rejected_by_me}</h3>
                  <p className="text-sm text-gray-500">Total rejected</p>
                </div>
                <div className="p-3 rounded-lg bg-red-600">
                  <XCircle className="w-6 h-6 text-white" />
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="flex border-b border-gray-200">
              <button
                onClick={() => setActiveTab('pending')}
                className={`flex-1 px-6 py-4 text-sm font-semibold transition-all ${
                  activeTab === 'pending'
                    ? 'text-white'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
                style={activeTab === 'pending' ? {backgroundColor: '#2b3380'} : {}}
              >
                <div className="flex items-center justify-center space-x-2">
                  <Clock className="w-5 h-5" />
                  <span>Pending Approvals ({stats.pending})</span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab('my-requests')}
                className={`flex-1 px-6 py-4 text-sm font-semibold transition-all ${
                  activeTab === 'my-requests'
                    ? 'text-white'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
                style={activeTab === 'my-requests' ? {backgroundColor: '#2b3380'} : {}}
              >
                <div className="flex items-center justify-center space-x-2">
                  <FileText className="w-5 h-5" />
                  <span>My Requests</span>
                </div>
              </button>
            </div>

            {/* Filters & Search */}
            <div className="p-6 border-b border-gray-200 bg-gray-50">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                  <input
                    type="text"
                    placeholder="Search by name or description..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div className="relative">
                  <Filter className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                  <select
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
                  >
                    <option value="all">All Types</option>
                    <option value="leave">Leave</option>
                    <option value="expense">Expense</option>
                    <option value="hiring">Hiring</option>
                    <option value="confirmation">Confirmation</option>
                    <option value="exit">Exit</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              {filteredData.length === 0 ? (
                <div className="text-center py-16">
                  <AlertCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 text-lg">
                    {activeTab === 'pending' 
                      ? 'No pending approvals at the moment' 
                      : 'You haven\'t submitted any requests yet'}
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredData.map((approval) => (
                    <div
                      key={approval.id}
                      className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-all bg-white"
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-start space-x-4">
                          <div className="text-4xl">{getRequestTypeIcon(approval.request_type)}</div>
                          <div>
                            <h3 className="font-bold text-lg" style={{color: '#2b3380'}}>
                              {approval.request_type.replace(/_/g, ' ').toUpperCase()}
                            </h3>
                            <p className="text-sm text-gray-600 mt-1">{approval.description}</p>
                            <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                              <span className="flex items-center space-x-1">
                                <User className="w-4 h-4" />
                                <span>{approval.requester_name}</span>
                              </span>
                              <span className="flex items-center space-x-1">
                                <Calendar className="w-4 h-4" />
                                <span>{new Date(approval.created_at).toLocaleDateString()}</span>
                              </span>
                              {approval.amount > 0 && (
                                <span className="flex items-center space-x-1">
                                  <DollarSign className="w-4 h-4" />
                                  <span>₹{approval.amount.toLocaleString()}</span>
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div>
                          {getStatusBadge(approval.status)}
                        </div>
                      </div>

                      {/* Approval Chain */}
                      <div className="bg-gray-50 rounded-lg p-4 mb-4">
                        <p className="text-xs font-semibold text-gray-600 mb-2">APPROVAL CHAIN:</p>
                        <div className="flex items-center space-x-2 flex-wrap">
                          {approval.approval_chain.map((role, idx) => (
                            <React.Fragment key={idx}>
                              <span className={`px-3 py-1 rounded text-xs font-semibold ${
                                idx < approval.current_step 
                                  ? 'bg-green-100 text-green-800'
                                  : idx === approval.current_step
                                  ? 'bg-blue-100 text-blue-800 ring-2 ring-blue-500'
                                  : 'bg-gray-100 text-gray-500'
                              }`}>
                                {role.replace(/_/g, ' ').toUpperCase()}
                              </span>
                              {idx < approval.approval_chain.length - 1 && (
                                <ChevronRight className="w-4 h-4 text-gray-400" />
                              )}
                            </React.Fragment>
                          ))}
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                          Current: <strong>{approval.current_approver_role.replace(/_/g, ' ').toUpperCase()}</strong> | 
                          Step {approval.current_step + 1} of {approval.approval_chain.length}
                        </p>
                      </div>

                      {/* Action Buttons */}
                      {activeTab === 'pending' && approval.status === 'pending' && (
                        <div className="flex items-center space-x-3">
                          <button
                            onClick={() => openActionModal(approval, 'approve')}
                            className="flex-1 py-2 px-4 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors flex items-center justify-center space-x-2"
                          >
                            <CheckCircle className="w-5 h-5" />
                            <span>Approve</span>
                          </button>
                          <button
                            onClick={() => openActionModal(approval, 'reject')}
                            className="flex-1 py-2 px-4 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-colors flex items-center justify-center space-x-2"
                          >
                            <XCircle className="w-5 h-5" />
                            <span>Reject</span>
                          </button>
                        </div>
                      )}

                      {/* History */}
                      {approval.history && approval.history.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          <p className="text-xs font-semibold text-gray-600 mb-2">APPROVAL HISTORY:</p>
                          <div className="space-y-2">
                            {approval.history.map((h, idx) => (
                              <div key={idx} className="flex items-start space-x-2 text-sm">
                                {h.action === 'approve' ? (
                                  <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
                                ) : (
                                  <XCircle className="w-4 h-4 text-red-600 mt-0.5" />
                                )}
                                <div>
                                  <p className="text-gray-700">
                                    <strong>{h.approver_name}</strong> ({h.approver_role}) {h.action}d
                                  </p>
                                  {h.comments && (
                                    <p className="text-gray-500 italic">"{h.comments}"</p>
                                  )}
                                  <p className="text-xs text-gray-400">{new Date(h.timestamp).toLocaleString()}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Action Modal */}
      {showActionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200" style={{backgroundColor: actionType === 'approve' ? '#e8f5e9' : '#ffebee'}}>
              <h2 className="text-2xl font-bold" style={{color: actionType === 'approve' ? '#2e7d32' : '#c62828'}}>
                {actionType === 'approve' ? '✅ Approve Request' : '❌ Reject Request'}
              </h2>
            </div>
            <div className="p-6">
              <p className="text-gray-700 mb-4">
                <strong>Request:</strong> {selectedApproval?.description}
              </p>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Comments {actionType === 'reject' && '(Required)'}:
              </label>
              <textarea
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                placeholder="Add your comments here..."
                rows="4"
                className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="p-6 border-t border-gray-200 flex space-x-3">
              <button
                onClick={() => {
                  setShowActionModal(false);
                  setComments('');
                }}
                disabled={loading}
                className="flex-1 py-2 px-4 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAction}
                disabled={loading || (actionType === 'reject' && !comments)}
                className={`flex-1 py-2 px-4 text-white rounded-lg font-semibold transition-colors ${
                  actionType === 'approve' 
                    ? 'bg-green-600 hover:bg-green-700' 
                    : 'bg-red-600 hover:bg-red-700'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {loading ? 'Processing...' : `Confirm ${actionType === 'approve' ? 'Approval' : 'Rejection'}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

export default ApprovalDashboard;
